import { emptyStyleProfile, mockStyleProfile, styleProfileSchema, type StyleProfile } from '../domain/style/schema';
import type { PreparedImage } from './images';

export interface AnalyzeImagesInput {
  images: PreparedImage[];
  context?: string;
}

export interface AnalysisResult {
  profile: StyleProfile;
  provider: string;
  model: string;
  generatedAt: string;
  warnings: string[];
}

export interface VisualStyleAnalyzer {
  analyzeImages(input: AnalyzeImagesInput): Promise<AnalysisResult>;
}

export class MockVisualStyleProvider implements VisualStyleAnalyzer {
  async analyzeImages(input: AnalyzeImagesInput): Promise<AnalysisResult> {
    await new Promise((resolve) => window.setTimeout(resolve, 800));
    if (input.images.length < 1 || input.images.length > 5) throw new Error('Submit one to five images.');
    return {
      profile: mockStyleProfile(input.images.map((image, index) => image.id || `image-${index + 1}`)),
      provider: 'mock',
      model: 'deterministic-style-fixture-v0.2',
      generatedAt: new Date().toISOString(),
      warnings: ['Local mock mode: no pixels were analysed.'],
    };
  }
}

export class RemoteVisualStyleProvider implements VisualStyleAnalyzer {
  async analyzeImages(input: AnalyzeImagesInput): Promise<AnalysisResult> {
    const form = new FormData();
    input.images.forEach((image) => form.append('images', image.blob, `${image.name}.webp`));
    if (input.context) form.set('context', input.context);
    const response = await fetch('/api/style/analyze', { method: 'POST', body: form });
    const payload = (await response.json()) as Record<string, unknown>;
    if (!response.ok) throw new Error(String(payload.message ?? 'Visual analysis is unavailable.'));
    const profile = styleProfileSchema.parse(payload.profile);
    return {
      profile,
      provider: String(payload.provider),
      model: String(payload.model),
      generatedAt: String(payload.generatedAt),
      warnings: Array.isArray(payload.warnings) ? payload.warnings.map(String) : [],
    };
  }
}

export const getStyleAnalyzer = (): VisualStyleAnalyzer => {
  const configured = import.meta.env.VITE_AI_PROVIDER;
  const useCloudflare = configured === 'cloudflare' || (import.meta.env.PROD && configured !== 'mock');
  return useCloudflare ? new RemoteVisualStyleProvider() : new MockVisualStyleProvider();
};

export const manualAnalysisResult = (): AnalysisResult => ({
  profile: emptyStyleProfile(),
  provider: 'manual',
  model: 'none',
  generatedAt: new Date().toISOString(),
  warnings: ['Visual analysis was skipped.'],
});
